import { FC, useEffect, useRef, useState, useMemo } from 'react';
import { Button } from 'antd';
import { useMethods } from '@lhb/hook';
import { downloadFile, isArray, isNotEmptyAny, isNumber, refactorPermissions } from '@lhb/func';
import V2Message from '@/common/components/Others/V2Hint/V2Message';
import { ChangePonitDetailType } from '../type';
import { ChanceDetailModule, ChanceDetailPermission, RelationCheckPointsModule } from '@/common/components/business/ExpandStore/ts-config';
import { exportModelClusterPDFAsync } from '@/common/api/siteselectionmap';
import { CHANCEPOINT_REPORT_PDF, ChancePdfPageClass, PDFChanceModuleName, isShowProjectInfoPage } from '@/views/pdf/pages/chancepointreport/ts-config';
// import { getCookie } from '@lhb/cache';
import { getPDFExportStatus, getSiteModelList } from '@/common/api/networkplan';
import { useSelector } from 'react-redux';
import { useBigdataFn } from '@/common/utils/bigdata';
import ConfirmExport from './ConfirmExport';
import { usePageTitle } from '@/common/hook/usePageTitle';
// import HintModal from '@/common/components/Pdf/HintModal';

/** 模块组件映射关系 采用map映射方便后期维护 添加/删除 模块*/
const targetMap = {
  [ChanceDetailModule.Radar]: PDFChanceModuleName.MODULE_POINT_EVALUATION, // 点位评分
  [ChanceDetailModule.Detail]: PDFChanceModuleName.MODULE_DETAIL, // 动态表单
  [ChanceDetailModule.BusinessAreaModule]: PDFChanceModuleName.MODULE_BUSINESS_AREA, // 商圈
  [ChanceDetailModule.Basic]: PDFChanceModuleName.MODULE_PROJECT_INFO, // 核心信息
  [ChanceDetailModule.Benifit]: PDFChanceModuleName.MODULE_PROJECT_INFO, // 收益预估
  [ChanceDetailModule.Overview]: PDFChanceModuleName.MODULE_PROJECT_INFO, // 项目综述
  [ChanceDetailModule.RelationCheckPoints]: PDFChanceModuleName.MODULE_CHECKPOINTS, // 客流计数
};

/** 组件传递参数类型 */
interface ExportProps {
  detail: ChangePonitDetailType; // 详情
  approvalDetail: any; // 审批详情
}

/**
 * @Description 标准版本-机会点详情-顶部-生成按钮
 */
const Export: FC<ExportProps> = ({
  detail,
  approvalDetail,
}) => {
  const bigdataFn = useBigdataFn(); // 使用埋点 Hook
  const modelConfigId = useSelector((state: any) => state.iterate.modelConfigId); // 存放选址地图页面的当前模型
  const currentUserInfo = useSelector((state: any) => state.common.currentUserInfo) || null;
  const tenantCheckData = useSelector((state: any) => state.common.tenantCheck);
  const intervalForPdfRef: any = useRef();
  const [pdfDataStatus, setPdfDataStatus] = useState<any>(); // 机会点报告
  const { moduleDetails, checkSpots } = detail || {};
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [openConfirmExport, setOpenConfirmExport] = useState<boolean>(false);
  const { name: menuName } = usePageTitle();

  /**
   *  所有详情模块（接口返回+前端额外添加）
   *
   *   1、对于接口返回的详情模块，过滤不展示的模块：不支持提交审批时，有配置就展示；支持审批时，模块可见时才展示supportDirectApproval 是否支持直接提交审批 1:支持 2:不支持
   *   2、额外模块-客流计数的展示前提：yht、展示动态表单详情模块、有关联的踩点点时位
   */
  const moduleDetailsArray = useMemo(() => {
    // 接口返回数据
    const arr: any[] = isArray(moduleDetails) ? moduleDetails.filter((module: any) => detail.supportDirectApproval === 2 || [1, 2].includes(module.isShow)) : [];

    // 展示客流计数模块的判断，yht、展示动态表单详情模块、有关联的踩点点位
    const isYiHeTang = !!tenantCheckData?.isYiHeTang;
    const detailModuleIndex = arr.findIndex((module: any) => module.moduleType === ChanceDetailModule.Detail);
    const showRelationCheckpoints = isYiHeTang && (detailModuleIndex >= 0) && isArray(checkSpots) && checkSpots.length;
    if (showRelationCheckpoints) {
      arr.splice(detailModuleIndex, 0, RelationCheckPointsModule);
    }
    return arr;
  }, [moduleDetails, checkSpots, tenantCheckData]);

  const waterText = useMemo(() => {
    if (currentUserInfo?.name) {
      if (currentUserInfo?.mobile) {
        return `${currentUserInfo.name} ${currentUserInfo.mobile.slice(-4)}`;
      }
      return currentUserInfo.name;
    }
    return undefined;
  }, [currentUserInfo]);
  const methods = useMethods({
    // 生成报告
    handleReportExport: async () => {
      if (!isNotEmptyAny(approvalDetail)) {
        // 无审批信息，不弹是否生成审批意见确认框
        methods.confirmReportExport(false);
        return;
      }
      // 确认框
      setOpenConfirmExport(true);
    },

    /**
     * @description 确认生成报告
     * @param pdfShowApproval 是否生成机会点审批通过信息
     * @return
     */
    confirmReportExport: async (pdfShowApproval: boolean) => {
      setIsExporting(true);
      let targetModelConfigId = modelConfigId;
      // 因为有可能没有配置选址地图页或者是未访问过选址地图页直接来到机会点详情进行生成
      if (!modelConfigId || +targetModelConfigId <= 0) {
        // 默认取模型第一个
        const data = await getSiteModelList();
        // 如果连模型都没配置，生成的报告里就不会显示商圈模块
        targetModelConfigId = isArray(data) ? data[0]?.id : 0;
      }
      const targetOptions = methods.getTargetOptions(targetModelConfigId, pdfShowApproval);

      await exportModelClusterPDFAsync({
        name: `点位评估报告${detail.name ? `-${detail.name}` : ''}.pdf`,
        type: 2, // 生成类型：1:商圈 2:机会点
        id: detail.id,
        nodeData: {
          needPageTable: true,
          pageTableCustomConfig: {
            x: 10,
            y: 10
          },
          options: targetOptions,
          name: `点位评估报告${detail.name ? `-${detail.name}` : ''}.pdf`,
          waterMark: waterText,
        },
        menuName
      }).finally(() => {
        // setShowHintModal(true);
        V2Message.success('报告正在生成中，请稍后查看');
        // 轮训状态
        methods.getPDFStatus();
        bigdataFn('2f3ff872-617a-4b2a-ae55-d40e9f157106', '机会点详情-生成报告', '', { op_block: '机会点管理', op_event: ``, });
      });
    },

    testMergeApproval(moduleNames) {
      const last = moduleNames[moduleNames.length - 1];
      if (last) {
        if (isNumber(last)) {
          if (last === 2 || last === 5) { // 最后一个是项目信息或者详情信息 就把他和审批合并
            return `${last},${PDFChanceModuleName.MODULE_APPROVAL}`;
          }
        } else {
          const target = last.split(',');
          if (target[target.length - 1] === '2' || target[target.length - 1] === '5') {
            return `${last},${PDFChanceModuleName.MODULE_APPROVAL}`;
          }
        }
      }
      return PDFChanceModuleName.MODULE_APPROVAL;
    },

    getTargetOptions: (targetModelConfigId: number, pdfShowApproval: boolean) => {
      const options: any[] = [];
      let moduleNames: any[] = [PDFChanceModuleName.MODULE_PRE];

      let flag = false;
      // 只显示部分配置的模块
      moduleDetailsArray.forEach((item: any) => {
        if (targetMap[item.moduleType]) {
          if (flag && targetMap[item.moduleType] === PDFChanceModuleName.MODULE_PROJECT_INFO) return; // 核心信息 + 收益预估 + 项目综述只添加一次
          if (targetMap[item.moduleType] === PDFChanceModuleName.MODULE_PROJECT_INFO) {
            isShowProjectInfoPage(moduleDetailsArray) && (flag = true);
          }
          if (item.moduleType === ChanceDetailModule.BusinessAreaModule && !detail.modelClusterId) return; // 配置了商圈但是没有商圈id时不显示

          if (!flag && targetMap[item.moduleType] === PDFChanceModuleName.MODULE_PROJECT_INFO) return; // 核心信息 + 收益预估 + 项目综述,非必要时不添加改页

          moduleNames.push(targetMap[item.moduleType]);
        }
      });

      const index1 = moduleNames.indexOf(targetMap[1]); // 项目信息
      const index2 = moduleNames.indexOf(targetMap[4]); // 详情信息
      if (index1 !== -1 && index2 !== -1) { // 如果项目信息和详情信息是一起的，且中间没有别的模块，就合并他们两
        if (index1 + 1 === index2) { // 这时候项目信息在详情信息前面
          const first = moduleNames.slice(0, index1);
          const last = moduleNames.slice(index2 + 1);
          moduleNames = [...first, `${moduleNames[index1]},${moduleNames[index2]}`, ...last];
        } else if (index1 - 1 === index2) { // 这时候详情信息在项目信息前面
          const first = moduleNames.slice(0, index2);
          const last = moduleNames.slice(index1 + 1);
          moduleNames = [...first, `${moduleNames[index2]},${moduleNames[index1]}`, ...last];
        }
      }
      // 用户选择了生成审批信息、且有审批信息时，展示审批通过信息
      if (pdfShowApproval) {
        const approval = methods.testMergeApproval(moduleNames);
        if (isNumber(approval)) { // 单独的审批模块
          moduleNames.push(approval);
        } else {
          moduleNames[moduleNames.length - 1] = approval;
        }
      }

      moduleNames.forEach(moduleName => {
        const url = [`${process.env.CONSOLE_PC_URL}/pdf/chancepointreport?id=${detail.id}&modelConfigId=${targetModelConfigId}&moduleName=${moduleName}`];
        // 非合并的，就用定制化，合并的就用标准化pdf（ps：详情信息除外，这个直接用标准化）
        if (isNumber(moduleName) && moduleName !== targetMap[4]) {
          // 定制化模块，并合并至最终的pdf里
          if (moduleName === 4) { // 商圈信息模块，比较特殊，需要拆分成三个小模块(避免商圈内部热力图同时渲染多个)
            // 商圈热力、经营门店、周边配套
            [1, 2, 3].forEach(index => {
              options.push({
                url: [`${url[0]}&localModule=${index}`],
                containerClass: CHANCEPOINT_REPORT_PDF,
                childClass: ChancePdfPageClass,
              });
            });
          } else {
            options.push({
              url,
              containerClass: CHANCEPOINT_REPORT_PDF,
              childClass: ChancePdfPageClass,
            });
          }
        } else {
          options.push({
            url,
            type: 'easy',
            width: '595px',
            // 如果表单字段少，bottom 会导致底部 logo 溢出到第二页，看上去是空白的第二页
            height: '1146px',
            bottom: 40,
          });
        }
      });
      return options;
    },

    // 下载报告
    handleDownload: () => {
      const { name, url } = pdfDataStatus;
      bigdataFn('99f27f77-9615-48c2-ae03-7ed34ba582c8', '机会点详情-查看报告', '', { op_block: '机会点管理', op_event: ``, });
      /*
        preview-pdf工具文档
        目前使用了其中阿里云文件处理
        https://docs.lanhanba.net/fe/staticres?name=preview-pdf
      */
      downloadFile({
        name,
        url: `${process.env.STATIC_RES_DOMAIN}//libs/preview-pdf/web/viewer.html?file=${encodeURIComponent(url)}&fixname=` + encodeURIComponent(`${name}`)
      });
    },

    getPDFStatus: async () => {
      const data = await getPDFExportStatus({
        id: detail.id,
        type: 2, // 1:商圈 2:机会点
      });
      // exportStatus: 1:未生成 2: 生成完成 3: 生成中
      const { exportStatus } = data;
      setPdfDataStatus(data);
      // 当前是生成中而且还未创建定时器轮训
      if (exportStatus === 3 && !intervalForPdfRef.current) {
        intervalForPdfRef.current = setInterval(() => {
          methods.getPDFStatus();
        }, 5000);
      } else if (exportStatus === 2) { // 生成完成后，清空定时器
        intervalForPdfRef.current && clearInterval(intervalForPdfRef.current);
        intervalForPdfRef.current = null;
      }
    },
  });

  /**
   * @description 是否有pdf生成按钮权限，有权限才展示生成报告、查看报告按钮
   */
  const hasPdfExportPermission = useMemo(() => {
    const { permissions } = detail || {};
    if (isArray(permissions) && permissions.length) {
      const refactorPerms = refactorPermissions(permissions);
      return !!refactorPerms.find((itm: any) => itm.event === ChanceDetailPermission.ReportExport);
    }
    return false;
  }, [detail]);

  const showDownloadBtn = useMemo(() => {
    const { exportStatus, name, url } = pdfDataStatus || {};
    return exportStatus === 2 && name && url;
  }, [pdfDataStatus]);

  // 编辑/导入权限
  useEffect(() => {
    detail && methods.getPDFStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail]);

  useEffect(() => {
    return () => {
      intervalForPdfRef.current && clearInterval(intervalForPdfRef.current);
      intervalForPdfRef.current = null;
    };
  }, []);

  useEffect(() => {
    const { exportStatus } = pdfDataStatus || {};
    if (exportStatus === 3) {
      setIsExporting(true);
    } else {
      setIsExporting(false);
    }
  }, [pdfDataStatus]);

  return (
    hasPdfExportPermission ? <div>
      <Button
        loading={isExporting}
        disabled={pdfDataStatus?.exportStatus === 3}
        onClick={methods.handleReportExport}
      >
        生成报告
      </Button>
      {/* {
        showDownloadBtn
          ? <span className='c-006 pointer ml-8 mr-8 fs-14 no-wrap' onClick={methods.handleDownload}>查看报告</span> : <></>
      } */}
      {/* 和选址地图青春版详情保持一致的样式 */}
      {
        showDownloadBtn
          ? <Button
            onClick={methods.handleDownload}
            className='ml-8'
          >
          查看报告
          </Button> : <></>
      }
      {/* 确认是否生成审批信息 */}
      <ConfirmExport
        open={openConfirmExport}
        setOpen={setOpenConfirmExport}
        handleNo={() => methods.confirmReportExport(false)}
        handleYes={() => methods.confirmReportExport(true)}
      />
    </div> : <></>
  );
};

export default Export;
